/**
 * The signed-in session, on the client. `TEN-001`, `TEN-005`, `OFF-001a`.
 *
 * ---
 *
 * **Why this file is allowed to call the API directly when nothing else is.**
 *
 * `OFF-001a` is "all writes go through the queue abstraction, with no direct API calls from the
 * UI", and the reason it gives is that this makes the later swap to a local-first store contained.
 * A login is the one mutating request that reason does not cover: it cannot be queued, because
 * there is nothing to do while it is pending and no offline answer to give. A queued login would be
 * a shop staring at a screen that never lets them in.
 *
 * So this is an exception, and it is named as one in `apps/counter/eslint.config.js` alongside the
 * transport rather than being quietly permitted by a directory glob. Everything that writes shop
 * data still goes through the queue.
 *
 * **The token is stored per tenant, and only after it is known.** A shared tablet running two shops
 * must not have one signing the other out, which is the same reason the write queue's IndexedDB
 * database name carries the tenant.
 */

const STORAGE_PREFIX = 'lensmira.session.'

export interface Session {
  readonly tenantId: string
  /** `<tenantId>.<secret>`. Sent as `Authorization: Bearer`. */
  readonly token: string
}

export type LoginResult =
  | { readonly kind: 'authenticated'; readonly session: Session }
  /** The credential was right and the second factor is being asked for. Not a failure. */
  | { readonly kind: 'totp_required' }
  /**
   * The credential was right and the code was not. Kept apart from `rejected` so the screen asks
   * for the code again instead of telling a shopkeeper their password is wrong: reaching this at
   * all requires the password to have verified, so the distinction discloses nothing the
   * `totp_required` step did not already.
   */
  | { readonly kind: 'totp_invalid'; readonly message: string }
  /**
   * The credential was right and this shop asks this role to enrol an authenticator first.
   * `TEN-005`, `decisions/0014`.
   *
   * Kept apart from `rejected` so the screen opens the enrolment panel instead of saying the
   * password was wrong. It was not wrong, and the panel is the thing the person needs.
   */
  | { readonly kind: 'enrolment_required'; readonly message: string }
  | { readonly kind: 'rejected'; readonly message: string }
  /** `DIR-003`: an unreachable directory must never look like a wrong password. */
  | {
      readonly kind: 'unavailable'
      readonly message: string
      /**
       * `network` when this client could not reach the server at all, so the sentence is one this
       * file made up and the screen can say it in the shop's language (`LOC-001`). Absent when the
       * server answered 503, in which case the message is the server's own words and travels
       * untouched, like every other Appendix F message.
       */
      readonly reason?: 'network'
    }

interface LoginResponseBody {
  readonly status?: string
  readonly sessionToken?: string
  readonly tenantId?: string
  readonly code?: string
  readonly message?: string
}

export interface LoginRequest {
  readonly identifier: string
  readonly password: string
  readonly totpCode?: string
}

/** What `POST /auth/totp/enrolment` can answer. The secret comes back once and is never read. */
export type EnrolResult =
  | { readonly kind: 'enrolled'; readonly secret: string; readonly uri: string }
  | { readonly kind: 'already_enrolled'; readonly message: string }
  | { readonly kind: 'rejected'; readonly message: string }
  | { readonly kind: 'unavailable'; readonly message: string }

interface EnrolResponseBody {
  readonly status?: string
  readonly secret?: string
  readonly uri?: string
  readonly message?: string
}

/**
 * Enrolling an authenticator, which is the one thing an owner cannot sign in without.
 *
 * `TEN-005` makes TOTP mandatory for the owner and the manager, and nothing in the product ever
 * wrote a secret, so both roles were refused with "that email address and password don't match"
 * while the password was right. The route is new; this is the client for it.
 */
export async function enrolTotp(
  baseUrl: string,
  request: { readonly identifier: string; readonly password: string },
  doFetch: typeof globalThis.fetch,
): Promise<EnrolResult> {
  let response: Response
  try {
    response = await doFetch(`${baseUrl}/auth/totp/enrolment`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
    })
  } catch {
    return {
      kind: 'unavailable',
      message:
        "We can't reach your account right now. This isn't your password, try again shortly.",
    }
  }

  const body = (await response.json().catch(() => ({}))) as EnrolResponseBody

  if (response.status === 503) {
    return { kind: 'unavailable', message: body.message ?? 'Your account is unreachable.' }
  }
  if (response.status === 409) {
    return {
      kind: 'already_enrolled',
      message: body.message ?? 'This account already has an authenticator.',
    }
  }
  if (!response.ok) return { kind: 'rejected', message: body.message ?? "That didn't work." }
  if (
    body.status === 'enrolled' &&
    typeof body.secret === 'string' &&
    typeof body.uri === 'string'
  ) {
    return { kind: 'enrolled', secret: body.secret, uri: body.uri }
  }
  // A 200 that carries no secret is a version mismatch, and showing nothing would read as success.
  return { kind: 'rejected', message: "That didn't work." }
}

export async function login(
  baseUrl: string,
  request: LoginRequest,
  doFetch: typeof globalThis.fetch,
): Promise<LoginResult> {
  let response: Response
  try {
    response = await doFetch(`${baseUrl}/auth/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
    })
  } catch {
    // A dead network on the login screen is not a wrong password, and DIR-003's whole point is that
    // the shop is never told it is.
    return {
      kind: 'unavailable',
      reason: 'network',
      message:
        "We can't reach your account right now. This isn't your password, try again shortly.",
    }
  }

  const body = (await response.json().catch(() => ({}))) as LoginResponseBody

  if (response.status === 503) {
    return {
      kind: 'unavailable',
      message: body.message ?? 'Your account is unreachable right now.',
    }
  }
  if (!response.ok) {
    // A refused code keeps the second-factor field on screen: the password was right, and asking
    // for it again is what makes a shopkeeper change one that was never wrong.
    if (body.status === 'totp_invalid') {
      return {
        kind: 'totp_invalid',
        message:
          body.message ??
          'That code was not accepted. Check your authenticator and try the current one.',
      }
    }
    // The password verified and the account has no authenticator in a shop that asks for one.
    // `rejected` here would read as a wrong password and send them to change one that works.
    if (body.status === 'enrolment_required') {
      return {
        kind: 'enrolment_required',
        message:
          body.message ??
          'Your password was accepted. This shop asks owners and managers to set up an ' +
            'authenticator app before signing in.',
      }
    }
    return { kind: 'rejected', message: body.message ?? "That didn't work." }
  }
  if (body.status === 'totp_required') return { kind: 'totp_required' }
  if (
    body.status === 'authenticated' &&
    typeof body.sessionToken === 'string' &&
    typeof body.tenantId === 'string'
  ) {
    return {
      kind: 'authenticated',
      session: { tenantId: body.tenantId, token: body.sessionToken },
    }
  }
  return { kind: 'rejected', message: "That didn't work." }
}

export interface PinLoginRequest {
  readonly terminalToken: string
  readonly username: string
  readonly pin: string
}

/**
 * `POST /auth/pin-login`. `TEN-006`, `decisions/0021`.
 *
 * Here with `login` for the same reason: signing in cannot be queued. The answer has the shape of
 * a login's, minus the second-factor outcomes, which a PIN never produces (an account with an
 * authenticator is refused a PIN, not asked for a code).
 *
 * **A refusal says nothing about which half was wrong**, and the screen must not add a guess. The
 * server answers a wrong PIN, an unknown name, a locked account and a revoked terminal alike.
 */
export async function pinLogin(
  baseUrl: string,
  request: PinLoginRequest,
  doFetch: typeof globalThis.fetch,
): Promise<LoginResult> {
  let response: Response
  try {
    response = await doFetch(`${baseUrl}/auth/pin-login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
    })
  } catch {
    return {
      kind: 'unavailable',
      reason: 'network',
      message: "We can't reach your account right now. This isn't your PIN, try again shortly.",
    }
  }

  const body = (await response.json().catch(() => ({}))) as LoginResponseBody

  if (response.status === 503) {
    return {
      kind: 'unavailable',
      message: body.message ?? 'Your account is unreachable right now.',
    }
  }
  if (!response.ok) return { kind: 'rejected', message: body.message ?? "That didn't work." }
  if (
    body.status === 'authenticated' &&
    typeof body.sessionToken === 'string' &&
    typeof body.tenantId === 'string'
  ) {
    return {
      kind: 'authenticated',
      session: { tenantId: body.tenantId, token: body.sessionToken },
    }
  }
  return { kind: 'rejected', message: "That didn't work." }
}

/** What `POST /terminals` can answer. The token comes back once and is never read again. */
export type EnrolTerminalResult =
  | { readonly kind: 'enrolled'; readonly name: string; readonly token: string }
  | { readonly kind: 'rejected'; readonly message: string }
  | { readonly kind: 'unavailable'; readonly message: string }

interface EnrolTerminalBody {
  readonly enrolled?: { readonly name?: unknown; readonly token?: unknown }
  readonly message?: string
}

/**
 * Enrolling a terminal. `TEN-006`.
 *
 * **Not through the write queue, and the reason is the response.** The queue keeps the server's
 * answer on the write record in IndexedDB until its retention lapses, and this answer carries the
 * only copy of the terminal's token. A record like that is a credential at rest on the owner's
 * device. The route is an owner acting at a screen, with nothing to do while it is pending, so it
 * is in the same position as a login and is named as an exception in the same place.
 *
 * The idempotency key is minted by the caller and kept across a retry, so a double tap enrols one
 * terminal. A replayed answer carries no token (the server never stored it), which this reports as
 * a refusal, because a screen that said "enrolled" with nothing to show would be a trap.
 */
export async function enrolTerminal(
  baseUrl: string,
  session: Session,
  request: { readonly branchId: string; readonly name: string },
  idempotencyKey: string,
  doFetch: typeof globalThis.fetch,
): Promise<EnrolTerminalResult> {
  let response: Response
  try {
    response = await doFetch(`${baseUrl}/terminals`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${session.token}`,
        'idempotency-key': idempotencyKey,
      },
      body: JSON.stringify(request),
    })
  } catch {
    return {
      kind: 'unavailable',
      message: "We can't reach the server right now. Try again shortly.",
    }
  }

  const body = (await response.json().catch(() => ({}))) as EnrolTerminalBody
  if (response.status === 503) {
    return { kind: 'unavailable', message: body.message ?? 'The server is unreachable.' }
  }
  if (!response.ok) return { kind: 'rejected', message: body.message ?? "That didn't work." }
  const name = body.enrolled?.name
  const token = body.enrolled?.token
  if (typeof name === 'string' && typeof token === 'string') {
    return { kind: 'enrolled', name, token }
  }
  return { kind: 'rejected', message: "That didn't work." }
}

const TERMINAL_KEY = 'lensmira.terminal'

/**
 * The enrolled terminal's token on this device, or null.
 *
 * `localStorage` for the same reasons as the session, and with the same wrapper: a private window
 * throws on the accessor itself. The token only lets a till offer PIN sign-in; it signs nobody in
 * without a person's short name and PIN, which is why it can live here at all.
 */
export function loadTerminalToken(): string | null {
  try {
    return window.localStorage.getItem(TERMINAL_KEY)
  } catch {
    return null
  }
}

export function storeTerminalToken(token: string): void {
  try {
    window.localStorage.setItem(TERMINAL_KEY, token)
  } catch {
    // Storage refused. The terminal still works until the page reloads, and the owner is told by
    // the sign-in screen not offering the PIN pad next time.
  }
}

export function forgetTerminalToken(): void {
  try {
    window.localStorage.removeItem(TERMINAL_KEY)
  } catch {
    // Nothing to do.
  }
}

/**
 * Persist the session so a page reload does not sign the shop out.
 *
 * `localStorage` and not a cookie, because the token is read by JavaScript to build the
 * `Authorization` header and a cookie would have to be readable to serve that, which gains nothing
 * and loses `HttpOnly`. The real protection is `TEN-005`'s session lifetime and the hash-at-rest in
 * the session table, both of which hold either way.
 *
 * Every access is wrapped, because a private window or a browser set to block site data throws on
 * the accessor itself rather than returning null.
 */
export function storeSession(session: Session): void {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${session.tenantId}`, session.token)
    window.localStorage.setItem(`${STORAGE_PREFIX}last`, session.tenantId)
  } catch {
    // Storage refused. The session still works for this tab, which is better than refusing to sign
    // in at all, and a reload will ask again.
  }
}

export function loadSession(): Session | null {
  try {
    const tenantId = window.localStorage.getItem(`${STORAGE_PREFIX}last`)
    if (tenantId === null) return null
    const token = window.localStorage.getItem(`${STORAGE_PREFIX}${tenantId}`)
    if (token === null) return null
    return { tenantId, token }
  } catch {
    return null
  }
}

export function clearSession(tenantId: string): void {
  try {
    window.localStorage.removeItem(`${STORAGE_PREFIX}${tenantId}`)
    window.localStorage.removeItem(`${STORAGE_PREFIX}last`)
  } catch {
    // Nothing to do, and nothing worth telling the shop about.
  }
}

/** What `POST /signup` can answer. `OPS-201`, `OPS-210`. */
export type SignUpResult =
  | {
      readonly kind: 'provisioning'
      readonly tenantId: string
      /** The address the owner signs in with, normalised by the server. */
      readonly signInAs: string
      readonly trialEndsAt: string
    }
  | { readonly kind: 'waitlisted'; readonly waitlistId: string }
  | { readonly kind: 'rejected'; readonly message: string }
  | { readonly kind: 'unavailable'; readonly message: string; readonly reason?: 'network' }

export interface SignUpRequest {
  readonly countryCode: string
  readonly email: string
  readonly shopName: string
  readonly ownerName: string
  readonly password: string
}

interface SignUpResponseBody {
  readonly status?: string
  readonly tenantId?: string
  readonly signInAs?: string
  readonly trialEndsAt?: string
  readonly waitlistId?: string
  readonly message?: string
}

/**
 * Signing a shop up, which is the only write an unauthenticated person can make.
 *
 * **A 202 rather than a 201, and the difference matters to this function.** `TEN-002a` provisions
 * in a background job, so when the server answers, the shop is in the directory and its database is
 * not built yet. The caller must not treat that as a session.
 *
 * `OPS-210`'s waitlist comes back as a 202 as well, because nothing went wrong: the signup was
 * accepted and the answer is that the country is not supported yet. Telling the two apart is the
 * `status` field, not the HTTP code.
 */
export async function signUp(
  baseUrl: string,
  request: SignUpRequest,
  doFetch: typeof globalThis.fetch,
): Promise<SignUpResult> {
  let response: Response
  try {
    response = await doFetch(`${baseUrl}/signup`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request),
    })
  } catch {
    return {
      kind: 'unavailable',
      reason: 'network',
      message: "We can't reach Lensmira right now. Try again shortly.",
    }
  }

  const body = (await response.json().catch(() => ({}))) as SignUpResponseBody

  if (response.status === 503) {
    return { kind: 'unavailable', message: body.message ?? 'Lensmira is unreachable.' }
  }
  if (!response.ok) return { kind: 'rejected', message: body.message ?? "That didn't work." }

  if (body.status === 'waitlisted' && typeof body.waitlistId === 'string') {
    return { kind: 'waitlisted', waitlistId: body.waitlistId }
  }
  if (
    body.status === 'provisioning' &&
    typeof body.tenantId === 'string' &&
    typeof body.signInAs === 'string' &&
    typeof body.trialEndsAt === 'string'
  ) {
    return {
      kind: 'provisioning',
      tenantId: body.tenantId,
      signInAs: body.signInAs,
      trialEndsAt: body.trialEndsAt,
    }
  }
  // An accepted response this client cannot read is a version mismatch, and reporting success
  // would send somebody to a sign-in screen for a shop that may not be coming.
  return { kind: 'rejected', message: "That didn't work." }
}
